import { Flex, Tag, Text } from "@chakra-ui/react";
import { Popover, Tooltip } from "antd";
import { ColumnType } from "antd/lib/table";
import { SimulacaoArrecadacaoItem } from "features/arrecadacoes/typings";
import { formatMoney } from "infra/helpers/number";
import moment from "moment";
import { useMemo } from "react";
import { AiFillExclamationCircle, AiOutlineCheck } from "react-icons/ai";
import colors from "core/resources/theme/colors";
import { formatBlocoQuadra } from "infra/helpers/formatBlocoQuadra";

function useTableSimulacaoColumns() {
  return useMemo((): ColumnType<SimulacaoArrecadacaoItem>[] => {
    return [
      {
        title: "",
        width: "32px",
        render: (original: SimulacaoArrecadacaoItem) => (
          <Flex flexDir="column">
            {original.erros.length ? (
              <Popover
                content={() => (
                  <Flex flexDir="column" p={2}>
                    {original.erros.map((erro) => (
                      <Text>{erro}</Text>
                    ))}
                  </Flex>
                )}
              >
                <AiFillExclamationCircle color={colors.red} size={24} />
              </Popover>
            ) : (
              <AiOutlineCheck color={colors.green500} size={24} />
            )}
          </Flex>
        ),
      },
      {
        title: "Vencimento",
        width: "90px",
        render: (original: SimulacaoArrecadacaoItem) => (
          <Flex flexDir="column">
            {moment(original.dataVencimento).format("DD/MM/YYYY")}
          </Flex>
        ),
      },
      {
        title: "Unidade",
        width: "150px",
        render: (original: SimulacaoArrecadacaoItem) => (
          <Flex>{formatBlocoQuadra(original.unidade, original.blocoQuadra)}</Flex>
        ),
      },
      {
        title: "Descrição",
        render: (original: SimulacaoArrecadacaoItem) => (
          <Flex flexDir="column">
            <Flex>{original.descricao}</Flex>
            {original.semBoleto ? (
              <Tooltip
                overlayInnerStyle={{ width: "max-content" }}
                placement="top"
                title="Essa cobrança tem data de vencimento anterior a hoje e será criada sem boleto registrado."
              >
                <Tag
                  rounded="full"
                  gap="4px"
                  w="fit-content"
                  variant="outline"
                  colorScheme="red"
                  textAlign="center"
                >
                  Sem boleto
                </Tag>
              </Tooltip>
            ) : null}
          </Flex>
        ),
      },
      {
        title: "Conta bancária",
        width: "155px",
        render: (original: SimulacaoArrecadacaoItem) => (
          <Flex>{original.tenantContaNome}</Flex>
        ),
      },
      {
        title: "Pagador",
        render: (original: SimulacaoArrecadacaoItem) => (
          <Flex gridGap={2} flexDirection="column">
            <Text>{original.nomePagador}</Text>
            <Flex gridGap={2} alignItems="center">
              {original.tipoPessoaPagador ? (
                <Tag
                  rounded="full"
                  gap="4px"
                  h="24px"
                  variant="outline"
                  colorScheme={"purple"}
                  textAlign="center"
                  w="fit-content"
                >
                  {getLabelTipoPessoaPagador(original.tipoPessoaPagador)}
                </Tag>
              ) : null}
              {original.descontoVariavel || original.retencoes?.length ? (
                <Tag
                  rounded="full"
                  gap="4px"
                  variant="outline"
                  colorScheme={"green"}
                  textAlign="center"
                  h="24px"
                  w="fit-content"
                >
                  Desconto
                </Tag>
              ) : null}
            </Flex>
          </Flex>
        ),
      },
      {
        title: "Valor",
        align: "right",
        width: "150px",
        render: (original: SimulacaoArrecadacaoItem) => (
          <Flex justifyContent="flex-end">{formatMoney(original.valor)}</Flex>
        ),
      },
    ];
  }, []);
}

const getLabelTipoPessoaPagador = (tipo: "inquilino" | "proprietario") => {
  if (tipo === "inquilino") {
    return "Inquilino";
  } else if (tipo === "proprietario") {
    return "Proprietário";
  }
};

export default useTableSimulacaoColumns;
